/**
 * ResetPasswordV2 — v2 reskin of auth/ResetPassword (UI-v2 Phase 1e).
 *
 * Logic verbatim from v1. Presentation: AuthShellV2 + shadcn primitives + FormFieldV2.
 */
import { useState } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useResetPassword } from "@/hooks/queries/auth/useResetPassword";
import { useToastStore } from "@/stores/useToastStore";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

export default function ResetPasswordV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const [searchParams] = useSearchParams();
  const resetPassword = useResetPassword();
  const addToast = useToastStore((state) => state.addToast);

  const token = searchParams.get("token") ?? "";
  const [newPassword, setNewPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError(null);

    if (!tenantSlug) return;

    resetPassword.mutate(
      { tenantSlug, token, new_password: newPassword },
      {
        onSuccess: () => {
          // AU4: AuthShellV2 does not host a Toast, so a toast-then-navigate
          // confirmation would never render. Show an inline success card that
          // is independent of any shell-level Toast mount. (The toast is still
          // fired so it also surfaces once the shell mounts one.)
          addToast("Password reset. Sign in with your new password.", "success");
          setDone(true);
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_token":
              setFieldError("This link is invalid or expired. Request a new one.");
              break;
            case "weak_password": {
              const reasons = Array.isArray(err.details["reasons"])
                ? (err.details["reasons"] as string[]).join(", ")
                : "Password is too weak.";
              setFieldError(reasons);
              break;
            }
            default:
              setFieldError("That didn't go through. Try again.");
          }
        },
      }
    );
  };

  if (done) {
    return (
      <AuthShellV2
        title="Password reset"
        subtitle="Your password has been updated."
        footer={
          <Link
            to={`${base}/login`}
            className="text-text-link underline hover:text-text-link-hover"
          >
            Back to sign in
          </Link>
        }
      >
        <p className="text-center text-sm text-text-secondary">
          Sign in with your new password to continue.
        </p>
      </AuthShellV2>
    );
  }

  if (!token) {
    return (
      <AuthShellV2 title="Set a new password" subtitle="Invalid reset link.">
        <p className="text-center text-sm text-text-secondary">
          Please use the link from your reset email.
        </p>
      </AuthShellV2>
    );
  }

  return (
    <AuthShellV2
      title="Set a new password"
      subtitle="Choose a strong password — at least 8 characters."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormFieldV2
          label="New password"
          htmlFor="newPassword-v2"
          error={fieldError ?? undefined}
        >
          <Input
            id="newPassword-v2"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            aria-invalid={!!fieldError || undefined}
          />
        </FormFieldV2>
        <Button
          type="submit"
          className="w-full"
          disabled={resetPassword.isPending}
          aria-busy={resetPassword.isPending}
        >
          {resetPassword.isPending ? "Resetting…" : "Reset password"}
        </Button>
      </form>
    </AuthShellV2>
  );
}
